import { FunctionalComponent, h } from 'preact';
import { Localizer } from 'preact-i18n';
import { AmLocations, Locations, LocationType, Properties, WidgetTab } from '../../common';
import { WidgetTestVariant } from '../../config';
import { Actions, actions, BookingState, connectStore } from '../../store';
import { WithStyles } from '../../style';
import { isPresent } from '../../utils';
import AmLocationModal from './LocationModal/AmLocationModal/AmLocationModal';
import { LocationModal } from './LocationModal/LocationModal';
import LocationSelectorBase from './LocationSelectorBase';
import { amLocationToItem, locationToItem } from './locationUtils';
import { ModalContext } from '../../modal';
import { FinText } from '../../i18n';

interface StoreProps {
  locations: Locations[];
  amLocations: AmLocations;
  onAmTab: boolean;
  enableCompactWidget: boolean;
}

export interface LocationSelectorProps extends Properties {
  locationType: LocationType;
  isAward?: boolean;
  testVariant: WidgetTestVariant;
  activeLocationIndex?: number;
}

type EffectiveProps = LocationSelectorProps & StoreProps & Actions & WithStyles;

const mapStateToProps = ({
  selection: { locations },
  amState: {
    amSelection: { locations: amLocations },
  },
  currentTab,
  enableCompactWidget,
}: BookingState): StoreProps => ({
  locations,
  amLocations,
  onAmTab: currentTab === WidgetTab.AM,
  enableCompactWidget,
});

const getAmLocation = (locationType: LocationType, amLocations: AmLocations) => {
  const { origin, destination } = amLocations;
  if (locationType === LocationType.ORIGIN) {
    return isPresent(origin) ? locationToItem(origin) : undefined;
  }
  return isPresent(destination) ? amLocationToItem(destination, amLocations.holidayType) : undefined;
};

const getLocation = (locationType: LocationType, locations: Locations) =>
  locations?.[locationType] ? locationToItem(locations[locationType]) : undefined;

export const LocationSelector: FunctionalComponent<LocationSelectorProps> = connectStore({ mapStateToProps, actions })(
  (
    {
      locationType,
      locations,
      amLocations,
      onAmTab,
      isAward,
      testVariant,
      enableCompactWidget,
      activeLocationIndex = 0,
      setLocationType,
      setActiveLocationIndex,
    }: EffectiveProps,
    context: ModalContext
  ) => {
    const location = onAmTab
      ? getAmLocation(locationType, amLocations)
      : getLocation(locationType, locations[activeLocationIndex]);
    const openModal = (): void => {
      setActiveLocationIndex(activeLocationIndex);
      setLocationType(locationType);
      if (onAmTab) {
        context.modal.showModal(<AmLocationModal testVariant={testVariant} locationType={locationType} />);
      } else {
        context.modal.showModal(
          <LocationModal testVariant={testVariant} locationType={locationType} isAward={isAward} />
        );
      }
    };

    return (
      <Localizer>
        <LocationSelectorBase
          location={location}
          text={<FinText id={`locationSelector.${locationType}.title`} />}
          onSelect={openModal}
          locationType={locationType}
          enableCompactWidget={enableCompactWidget}
        />
      </Localizer>
    );
  }
);
